import { useLingui } from '@lingui/react/macro'
import { useNavigate } from '@tanstack/react-router'
import { daysFromToday, getStatusMeta, type Service } from './utils'

/**
 * Shared presentation logic for a service row, used by both the desktop table
 * row and the mobile card so the status wording and navigation stay identical.
 */
export function useServiceDisplay(service: Service, companyId: string) {
  const { t } = useLingui()
  const navigate = useNavigate()
  const meta = getStatusMeta(service.status)
  const dueIn = daysFromToday(service.nextDueDate)

  const statusLabel = (() => {
    switch (service.status) {
      case 'overdue':
        return t`${Math.abs(dueIn ?? 0)} d overdue`
      case 'due_soon':
        return dueIn !== null && dueIn <= 0
          ? t`Due today`
          : t`Due in ${dueIn ?? 0} d`
      case 'scheduled':
        return t`Visit booked`
      case 'completed':
        return t`On track`
      default:
        return t`Not due yet`
    }
  })()

  const openVisit = (id: number) =>
    navigate({ to: `/company-portal/${companyId}/visits/${id}` })

  return {
    meta,
    statusLabel,
    openVisit,
    hasLast: !!(service.lastCompletedVisitId && service.lastCompletedDate),
    hasNext: !!(service.nextScheduledVisitId && service.nextScheduledDate),
  }
}
